import { useState, useEffect } from 'react';
import { COLUMNS, UNSECTIONED } from '../sections.js';
import TaskRow from './TaskRow.jsx';

/**
 * One section: a header and its rows.
 *
 * "New task" in the header is the only way in, and it opens the full form already filed here.
 * There used to be a quick-add row under the last task as well; two ways to add the same thing,
 * one of which left the module and description to be guessed at, is a choice nobody wanted to
 * make on every task.
 *
 * It holds no drag state. Which task is in the air and where it will land belongs to the page,
 * because a drop that crosses sections is a fact about two cards and neither one can see the
 * other's state — that is exactly why the same feature in Project Management cannot move a task
 * between sections at all.
 */
export default function SectionCard({
  section, tasks, options, overlaps, collapsed, onToggle,
  canDrag, sortedOff, drag, dropAt,
  onRename, onDelete, onFullForm,
  onTaskDragStart, onRowDragOver, onSectionDragOver, onDragEnd, onDrop,
  onPatchTask, onTransitionTask, savingIds,
  sectionDraggable, onSectionDragStart, onMoveUp, onMoveDown,
}) {
  const isLoose = section.id === null;
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(section.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { setName(section.name); }, [section.name]);

  const commitRename = async () => {
    setRenaming(false);
    const next = name.trim();
    if (!next || next === section.name) { setName(section.name); return; }
    await onRename(section, next);
  };

  return (
    <section
      className="o-panel"
      style={{ padding: 0, marginBottom: 10 }}
      onDragOver={onSectionDragOver}
      onDrop={onDrop}
    >
      <header style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
        borderBottom: collapsed ? 'none' : '1px solid var(--rule-soft)',
      }}>
        {!isLoose && (
          <span
            className="o-grip"
            draggable={sectionDraggable}
            aria-disabled={sectionDraggable ? undefined : 'true'}
            title={sectionDraggable
              ? 'Drag to reorder sections'
              : 'Sorting is on. Switch to Manual order to rearrange.'}
            onDragStart={onSectionDragStart}
            onDragEnd={onDragEnd}
          >&#8942;&#8942;</span>
        )}

        <button type="button" className="o-x" aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${section.name}` : `Collapse ${section.name}`}
          onClick={onToggle}>{collapsed ? '▸' : '▾'}</button>

        {renaming ? (
          <input className="o-control" autoFocus value={name} style={{ maxWidth: 280 }}
            onChange={e => setName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') { setName(section.name); setRenaming(false); }
            }} />
        ) : (
          <button type="button" className="o-strong"
            style={{ border: 0, background: 'none', padding: 0, font: 'inherit', fontWeight: 600,
                     cursor: isLoose ? 'default' : 'text', textAlign: 'left' }}
            title={isLoose ? undefined : 'Click to rename'}
            onClick={() => !isLoose && setRenaming(true)}>{section.name}</button>
        )}

        <span className="o-badge o-grey">{tasks.length}</span>

        {!isLoose && (
          <span className="o-reorder" style={{ display: 'inline-flex' }}>
            <button type="button" aria-label={`Move ${section.name} up`}
              disabled={!sectionDraggable} onClick={onMoveUp}>&#9650;</button>
            <button type="button" aria-label={`Move ${section.name} down`}
              disabled={!sectionDraggable} onClick={onMoveDown}>&#9660;</button>
          </span>
        )}

        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {isLoose && <span className="o-note" style={{ margin: 0 }}>{UNSECTIONED.note}</span>}
          {/* Opens the full form already filed into this section. The server places a new task
              at the end of the section it names, so it lands at the bottom of this card. */}
          <button type="button" className="o-btn o-btn-ghost o-btn-sm"
            onClick={() => onFullForm(section)}>New task</button>
          {!isLoose && !confirmDelete && (
            <button type="button" className="o-btn o-btn-ghost o-btn-sm"
              onClick={() => setConfirmDelete(true)}>Delete section</button>
          )}
        </span>
      </header>

      {confirmDelete && (
        <div className="o-hint" style={{ margin: 0, padding: '8px 12px',
          borderBottom: '1px solid var(--rule-soft)' }}>
          <b>Delete this section?</b> Its {tasks.length} task{tasks.length === 1 ? '' : 's'} move
          to Unsectioned — nothing is deleted.
          <span style={{ display: 'inline-flex', gap: 8, marginLeft: 10 }}>
            <button className="o-btn o-btn-danger o-btn-sm"
              onClick={() => { setConfirmDelete(false); onDelete(section); }}>Delete section</button>
            <button className="o-btn o-btn-ghost o-btn-sm"
              onClick={() => setConfirmDelete(false)}>Keep it</button>
          </span>
        </div>
      )}

      {!collapsed && (
        /* A few pixels under the last row. Without them the row sits on the card's own border
           and reads as cut off rather than as the end of the list. */
        <div style={{ paddingBottom: 6 }}>
          <table className="o-tbl o-tbl-tight">
            <colgroup>
              {COLUMNS.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
            </colgroup>
            <tbody>
              {tasks.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ padding: 0 }}>
                    <div className="o-drop" style={{ margin: 8, padding: 'var(--s3)' }}>
                      <span className="o-note" style={{ margin: 0 }}>
                        {drag ? 'Drop a task here.' : 'No tasks in this section yet.'}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {tasks.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  options={options}
                  overlaps={overlaps?.[task.id]}
                  draggable={canDrag}
                  sortedOff={sortedOff}
                  dragging={drag?.taskId === task.id}
                  saving={!!savingIds?.[task.id]}
                  onPatch={onPatchTask}
                  onTransition={onTransitionTask}
                  dropEdge={dropAt?.beforeTaskId === task.id ? 'before'
                    : (dropAt && dropAt.afterTaskId === task.id ? 'after' : null)}
                  onDragStart={e => onTaskDragStart(e, task, section.id)}
                  onDragOver={e => onRowDragOver(e, section.id, task)}
                  onDragEnd={onDragEnd}
                  onDrop={onDrop}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
